/**
 * Cloze-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Cloze game events.
 * Cloze only has one event type: 'submit' to validate word placements.
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent } = useGameplaySession('cloze', date);
 * const { submitPlacements } = useClozeEvents(dispatchEvent);
 * 
 * const result = await submitPlacements(['word1', 'word2', 'word3']);
 * if (result.success) {
 *   // result.result contains: { complete, feedback, lives_remaining, solutions }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Cloze-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @returns {object} Cloze event helpers
 * @returns {function} returns.submitPlacements - Submit current word placements for validation
 */
export function useClozeEvents(dispatchEvent) {
  /**
   * Submit current word placements for validation.
   * 
   * @param {string[]} words - Array of placed words (all blanks must be filled)
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     complete: boolean,
   *     feedback: Array<{state: 'correct' | 'incorrect'}>,
   *     lives_remaining: number,
   *     solutions?: string[]
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitPlacements(['sabato', 'zaini', 'alberi']);
   * if (result.success) {
   *   if (result.result.complete) {
   *     // All correct - game won!
   *   } else {
   *     // Check result.result.feedback for per-word feedback
   *     // result.state.lives shows remaining lives
   *   }
   * }
   */
  const submitPlacements = useCallback(async (words) => {
    return dispatchEvent('submit', { words });
  }, [dispatchEvent]);

  return {
    submitPlacements,
  };
}

